<template>
  <section class="visit-stats-page">
    <header class="visit-stats-header">
      <button class="visit-back" type="button" @click="$emit('back')">返回首页</button>
      <div class="visit-title">
        <span class="eyebrow">Visit Monitor</span>
        <h2>访客统计</h2>
        <p>这里可以查看今日、本周和本月的访问量。</p>
      </div>
      <el-button class="visit-refresh-btn" :icon="Refresh" :loading="visitSummary.loading" @click="$emit('refresh')">刷新统计</el-button>
    </header>

    <div class="visit-stat-grid">
      <article class="visit-stat-card primary">
        <span>今日访问量</span>
        <strong>{{ displayValue(visitSummary.todayVisits) }}</strong>
        <small>按访问记录统计</small>
      </article>
      <article class="visit-stat-card primary">
        <span>本周</span>
        <strong>{{ displayValue(visitSummary.weekVisits) }}</strong>
        <small>本周访问量</small>
      </article>
      <article class="visit-stat-card">
        <span>本月</span>
        <strong>{{ displayValue(visitSummary.monthVisits) }}</strong>
        <small>本月访问量</small>
      </article>
    </div>

    <!-- <div class="visit-stats-note">访客用于观察进入客户端的人数，访问次数用于观察页面被打开的总量。</div> -->
  </section>
</template>

<script setup lang="ts">
import { Refresh } from '@element-plus/icons-vue';

type VisitSummary = {
  visible: boolean;
  loading: boolean;
  todayVisitors: number;
  todayVisits: number;
  weekVisits: number;
  monthVisitors: number;
  monthVisits: number;
};

const props = defineProps<{
  visitSummary: VisitSummary;
}>();

defineEmits<{
  (e: 'back'): void;
  (e: 'refresh'): void;
}>();

const formatStat = (value: number) => Number(value || 0).toLocaleString('zh-CN');
const displayValue = (value: number) => (props.visitSummary.loading ? '--' : formatStat(value));
</script>

<style scoped lang="scss">
.visit-stats-page {
  max-width: 1320px;
  margin: 0 auto 18px;
  padding: 32px;
  border: 1px solid var(--line);
  background:
    linear-gradient(180deg, rgba(0, 240, 216, 0.05), rgba(157, 92, 255, 0.024)),
    var(--surface);
  backdrop-filter: blur(26px) saturate(125%);
  box-shadow:
    0 24px 80px rgba(0, 0, 0, 0.38),
    0 0 26px rgba(0, 240, 216, 0.055),
    inset 0 1px 0 rgba(0, 240, 216, 0.1);
  border-radius: 8px;
}

.visit-stats-header {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 18px;
}

.visit-back {
  min-height: 42px;
  padding: 0 16px;
  color: var(--accent);
  border: 1px solid rgba(0, 240, 216, 0.42);
  background: rgba(0, 240, 216, 0.09);
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.25s ease, background-color 0.25s ease, transform 0.25s ease;
}

.visit-back::before {
  content: "< ";
  font-weight: 700;
}

.visit-back:hover {
  border-color: rgba(0, 240, 216, 0.68);
  background: rgba(0, 240, 216, 0.14);
  transform: translateY(-1px);
}

.visit-title {
  min-width: 0;
}

.visit-title h2 {
  margin: 8px 0;
  font-size: clamp(34px, 4vw, 56px);
  line-height: 1;
  letter-spacing: 0;
}

.visit-title p {
  color: var(--muted);
  line-height: 1.7;
}

.visit-refresh-btn {
  --el-button-bg-color: rgba(34, 184, 255, 0.1);
  --el-button-border-color: rgba(34, 184, 255, 0.42);
  --el-button-text-color: var(--blue);
  --el-button-hover-bg-color: rgba(34, 184, 255, 0.17);
  --el-button-hover-border-color: rgba(34, 184, 255, 0.7);
  --el-button-hover-text-color: #bfeaff;
}

.visit-stat-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 28px;
}

.visit-stat-card {
  min-width: 0;
  min-height: 170px;
  padding: 20px;
  border: 1px solid rgba(0, 240, 216, 0.16);
  background: rgba(5, 10, 22, 0.48);
  border-radius: 8px;
}

.visit-stat-card.primary {
  border-color: rgba(0, 240, 216, 0.28);
  background:
    linear-gradient(180deg, rgba(0, 240, 216, 0.09), rgba(34, 184, 255, 0.045)),
    rgba(5, 10, 22, 0.5);
}

.visit-stat-card span,
.visit-stat-card small {
  display: block;
  color: var(--muted);
  font-size: 13px;
}

.visit-stat-card strong {
  display: block;
  margin: 18px 0 12px;
  color: var(--accent);
  font-size: clamp(34px, 5vw, 54px);
  line-height: 1;
  letter-spacing: 0;
  text-shadow: 0 0 22px rgba(0, 240, 216, 0.2);
}

.visit-stats-note {
  margin-top: 18px;
  padding: 14px 16px;
  color: var(--muted);
  line-height: 1.7;
  border-left: 2px solid var(--blue);
  background: rgba(34, 184, 255, 0.055);
  border-radius: 0 8px 8px 0;
}

@media (max-width: 980px) {
  .visit-stats-header {
    grid-template-columns: 1fr;
    align-items: flex-start;
  }

  .visit-stat-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 560px) {
  .visit-stats-page {
    padding: 22px;
  }

  .visit-stat-grid {
    grid-template-columns: 1fr;
  }
}
</style>
